import {
  Action,
  Cancel,
  Content,
  Description,
  Overlay,
  Portal,
  Root,
  Title,
  Trigger,
} from "@radix-ui/react-alert-dialog";
import { ComponentProps, FC } from "react";

import { variants } from "@/components/ui/button/variants";
import { cn } from "@/lib/utils";

type AlertDialogProps = ComponentProps<typeof Root>;
type AlertDialogTriggerProps = ComponentProps<typeof Trigger>;
type AlertDialogPortalProps = ComponentProps<typeof Portal>;
type AlertDialogOverlayProps = ComponentProps<typeof Overlay>;
type AlertDialogContentProps = ComponentProps<typeof Content>;
type AlertDialogHeaderProps = ComponentProps<"div">;
type AlertDialogFooterProps = ComponentProps<"div">;
type AlertDialogTitleProps = ComponentProps<typeof Title>;
type AlertDialogDescriptionProps = ComponentProps<typeof Description>;
type AlertDialogActionProps = ComponentProps<typeof Action>;
type AlertDialogCancelProps = ComponentProps<typeof Cancel>;

export const AlertDialog: FC<AlertDialogProps> = (props) => (
  <Root data-slot="alert-dialog" {...props} />
);

export const AlertDialogTrigger: FC<AlertDialogTriggerProps> = (props) => (
  <Trigger data-slot="alert-dialog-trigger" {...props} />
);

export const AlertDialogPortal: FC<AlertDialogPortalProps> = (props) => (
  <Portal data-slot="alert-dialog-portal" {...props} />
);

export const AlertDialogOverlay: FC<AlertDialogOverlayProps> = ({
  className,
  ...props
}) => (
  <Overlay
    data-slot="alert-dialog-overlay"
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80",
      className,
    )}
    {...props}
  />
);

export const AlertDialogContent: FC<AlertDialogContentProps> = ({
  className,
  ...props
}) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <Content
      data-slot="alert-dialog-content"
      className={cn(
        "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
        className,
      )}
      {...props}
    />
  </AlertDialogPortal>
);

export const AlertDialogHeader: FC<AlertDialogHeaderProps> = ({
  className,
  ...props
}) => (
  <div
    data-slot="alert-dialog-header"
    className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
    {...props}
  />
);

export const AlertDialogFooter: FC<AlertDialogFooterProps> = ({
  className,
  ...props
}) => (
  <div
    data-slot="alert-dialog-footer"
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className,
    )}
    {...props}
  />
);

export const AlertDialogTitle: FC<AlertDialogTitleProps> = ({
  className,
  ...props
}) => (
  <Title
    data-slot="alert-dialog-title"
    className={cn("text-lg font-medium", className)}
    {...props}
  />
);

export const AlertDialogDescription: FC<AlertDialogDescriptionProps> = ({
  className,
  ...props
}) => (
  <Description
    data-slot="alert-dialog-description"
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
);

export const AlertDialogAction: FC<AlertDialogActionProps> = ({
  className,
  ...props
}) => <Action className={cn(variants(), className)} {...props} />;

export const AlertDialogCancel: FC<AlertDialogCancelProps> = ({
  className,
  ...props
}) => (
  <Cancel
    className={cn(variants({ variant: "outline" }), className)}
    {...props}
  />
);
